/**
 * "Parameters" section of DashboardSettingsDialog (S10): declare, edit, and
 * remove dashboard parameters. Definitions save through the replay-safe
 * dashboard save; VALUES live in the uiState sidecar, not here.
 */
import { useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { DashboardParameter } from '../../types/dashboardTypes';
import { ParameterEditor } from './ParameterEditor';

type Props = {
  parameters: DashboardParameter[];
  onSave: (parameters: DashboardParameter[]) => void;
};

type EditorState = { index: number; draft: DashboardParameter } | null;

const KIND_LABEL: Record<DashboardParameter['kind'], string> = {
  'time-range': 'Time range',
  'static-list': 'List',
  'query-list': 'Query list',
  'free-text': 'Text',
};

const newParam = (kind: DashboardParameter['kind']): DashboardParameter => {
  switch (kind) {
    case 'time-range':
      return { kind, id: 'range', defaultDays: 7, allowCustom: true };
    case 'static-list':
      return { kind, id: '', label: '', values: [] };
    case 'query-list':
      return { kind, id: '', label: '', connectionId: '', sql: '' };
    case 'free-text':
      return { kind, id: '', label: '' };
  }
};

export function ParameterSettingsSection({ parameters, onSave }: Props) {
  const [editor, setEditor] = useState<EditorState>(null);
  const hasTimeRange = parameters.some((p) => p.kind === 'time-range');

  const commitDraft = (draft: DashboardParameter) => {
    if (!editor) return;
    const next = parameters.slice();
    if (editor.index === -1) next.push(draft);
    else next[editor.index] = draft;
    onSave(next);
    setEditor(null);
  };

  if (editor) {
    return (
      <ParameterEditor
        draft={editor.draft}
        siblings={parameters.filter((_, i) => i !== editor.index)}
        onCancel={() => setEditor(null)}
        onSave={commitDraft}
      />
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid="param-settings-section">
      <Label className="text-xs text-muted-foreground">Parameters</Label>
      {parameters.length === 0 && (
        <p className="text-xs text-muted-foreground">
          Parameters bind <code className="font-mono">{'{{name}}'}</code> tokens in tile queries and
          show as chips above the dashboard.
        </p>
      )}
      {parameters.map((param, index) => (
        <div
          key={param.kind === 'time-range' ? 'range' : param.id}
          className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5"
          data-testid={`param-row-${param.kind === 'time-range' ? 'range' : param.id}`}
        >
          <Badge variant="outline" className="text-[10px]">
            {KIND_LABEL[param.kind]}
          </Badge>
          <span className="flex-1 truncate text-xs font-medium">
            {param.kind === 'time-range' ? 'range' : param.id}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            aria-label="Edit parameter"
            data-testid={`param-edit-${param.kind === 'time-range' ? 'range' : param.id}`}
            onClick={() => setEditor({ index, draft: param })}
          >
            <Pencil className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-destructive"
            aria-label="Remove parameter"
            data-testid={`param-remove-${param.kind === 'time-range' ? 'range' : param.id}`}
            onClick={() => onSave(parameters.filter((_, i) => i !== index))}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      ))}
      {/* Dropdown-trigger idiom, one signal only: the chevron says "this opens
          a list of kinds". The former leading `+` made the same control read
          as a plain add BUTTON as well (S24 review finding). */}
      <Select value="" onValueChange={(kind) => setEditor({ index: -1, draft: newParam(kind as DashboardParameter['kind']) })}>
        <SelectTrigger className="h-7 w-44 text-xs" aria-label="Add parameter" data-testid="param-add-select">
          <SelectValue placeholder="Add parameter…" />
        </SelectTrigger>
        <SelectContent>
          {!hasTimeRange && <SelectItem value="time-range">Time range</SelectItem>}
          <SelectItem value="static-list">List (fixed values)</SelectItem>
          <SelectItem value="query-list">List (from query)</SelectItem>
          <SelectItem value="free-text">Free text</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
